import { type Ref, forwardRef } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import getFieldError from '@/shared/form/helpers/getFieldError';
import useMapCustomValidators from '@/shared/form/hooks/useMapCustomValidators';
import type { InputProps } from '@/shared/form/types';

import { TextInput as MantineTextInput, type TextInputProps as MantineTextInputProps } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';

export type TextInputProps<T extends FieldValues> = InputProps<UseControllerProps<T>> &
  Omit<MantineTextInputProps, 'value' | 'defaultValue' | 'onChange'>;

export default forwardRef(function Input<T extends FieldValues>(
  { name, control, defaultValue, shouldUnregister, onChange, ...props }: TextInputProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const rules = useMapCustomValidators(props, true);

  const {
    field: { value, onChange: fieldOnChange, ref: insideRef, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const mergedRefs = useMergedRef(insideRef, ref);

  return (
    <MantineTextInput
      ref={mergedRefs}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(name, e.target.value);
      }}
      error={getFieldError(fieldState)}
      mb="md"
      data-testid={name}
      {...field}
      {...props}
    />
  );
});
